import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import styles from './CRMLeadManagementSection.module.css';

gsap.registerPlugin(ScrollTrigger);

const INITIAL_LEADS = [
  { id: 1, name: "Alice Vance", email: "alice.vance@techcorp.com", status: "Imported", openCount: 0, clickCount: 0, verified: true, timeline: ["Lead imported via Excel"] },
  { id: 2, name: "Marcus Brody", email: "marcus.brody@archaeology.org", status: "Imported", openCount: 0, clickCount: 0, verified: false, timeline: ["Lead imported via Excel"] },
  { id: 3, name: "Elena Fisher", email: "elena@fishermedia.net", status: "Imported", openCount: 0, clickCount: 0, verified: true, timeline: ["Lead imported via Excel"] }
];

const CRMLeadManagementSection = () => {
  const [leads, setLeads] = useState(INITIAL_LEADS);
  const [simulationStep, setSimulationStep] = useState(0);
  const [isSimulating, setIsSimulating] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState(1);
  
  const sectionRef = useRef(null);
  const cardsRef = useRef(null);
  const simPanelRef = useRef(null);

  useEffect(() => {
    // Scroll entry animations
    const cards = cardsRef.current.querySelectorAll(`.${styles.featureCard}`);
    gsap.fromTo(
      cards,
      { opacity: 0, x: -30 },
      {
        opacity: 1,
        x: 0,
        duration: 0.8,
        ease: "power2.out",
        stagger: 0.15,
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 75%"
        }
      }
    );

    gsap.fromTo(
      simPanelRef.current,
      { opacity: 0, scale: 0.95 },
      {
        opacity: 1,
        scale: 1,
        duration: 1,
        ease: "back.out(1.1)",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 70%"
        }
      }
    );
  }, []);

  const handleStartSimulation = () => {
    if (isSimulating) return;
    setIsSimulating(true);
    setSimulationStep(1);
    setLeads(INITIAL_LEADS);
    setSelectedLeadId(1);
  };

  useEffect(() => {
    if (!isSimulating) return;

    let timer;
    if (simulationStep === 1) {
      // Step 1: SMTP Email Verification
      timer = setTimeout(() => {
        setLeads(prev => prev.map(l => {
          if (l.id === 2) return { ...l, verified: true, status: "Verified", timeline: [...l.timeline, "SMTP handshakes validated (Mailbox exists)"] };
          return { ...l, status: "Verified" };
        }));
        setSimulationStep(2);
      }, 2000);
    } else if (simulationStep === 2) {
      // Step 2: Send Bulk Email
      timer = setTimeout(() => {
        setLeads(prev => prev.map(l => ({
          ...l,
          status: "Sent",
          timeline: [...l.timeline, "📧 Sent Personalized Campaign Email"]
        })));
        setSimulationStep(3);
      }, 2000);
    } else if (simulationStep === 3) {
      // Step 3: Lead opens email (Alice and Elena open)
      timer = setTimeout(() => {
        setLeads(prev => prev.map(l => {
          if (l.id === 1 || l.id === 3) {
            return {
              ...l,
              status: "Opened",
              openCount: 1,
              timeline: [...l.timeline, "👁️ Opened Email (Tracking pixel triggered)"]
            };
          }
          return l;
        }));
        setSimulationStep(4);
      }, 2000);
    } else if (simulationStep === 4) {
      // Step 4: Click Link
      timer = setTimeout(() => {
        setLeads(prev => prev.map(l => {
          if (l.id === 1) {
            return {
              ...l,
              status: "Clicked Link",
              clickCount: 1,
              timeline: [...l.timeline, "🔗 Clicked Proposal Link: https://toxbox.ai/proposal"]
            };
          }
          return l;
        }));
        setSimulationStep(5);
      }, 2000);
    } else if (simulationStep === 5) {
      // Step 5: Hot Lead Handoff to AI Agent / Live Call
      timer = setTimeout(() => {
        setLeads(prev => prev.map(l => {
          if (l.id === 1) {
            return {
              ...l,
              status: "AI Followed Up",
              timeline: [...l.timeline, "📞 Call scheduled: AI Agent follow-up completed successfully"]
            };
          }
          return l;
        }));
        setIsSimulating(false);
        setSimulationStep(6);
      }, 2500);
    }

    return () => clearTimeout(timer);
  }, [isSimulating, simulationStep]);

  const selectedLead = leads.find(l => l.id === selectedLeadId) || leads[0];

  return (
    <section ref={sectionRef} className={styles.section}>
      <div className={styles.container}>
        {/* Left Side: CRM Features Description */}
        <div className={styles.featuresContent}>
          <div className={styles.badge}>Next-Gen CRM</div>
          <h2 className={styles.headline}>
            Close more deals with <br />
            <span className={styles.gradientText}>Intelligent Lead Tracking.</span>
          </h2>
          <p className={styles.subheadline}>
            No more guess work. Track every interaction from inbox deliverability and URL clicks, straight through to outbound AI voice agent calls.
          </p>

          <div ref={cardsRef} className={styles.featureCardsGrid}>
            <div className={styles.featureCard}>
              <div className={styles.iconContainer}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 12h20M12 2v20M20 6H4M4 18h16"/></svg>
              </div>
              <div>
                <h3 className={styles.cardTitle}>Dynamic Link & Open Tracking</h3>
                <p className={styles.cardDesc}>Know exactly when emails are opened and which links are clicked with background trackers.</p>
              </div>
            </div>

            <div className={styles.featureCard}>
              <div className={styles.iconContainer}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 12l2 2 4-4"/></svg>
              </div>
              <div>
                <h3 className={styles.cardTitle}>Multi-Stage SMTP Verification</h3>
                <p className={styles.cardDesc}>Pre-verify lead deliverability to protect your sender reputation and eliminate bounces.</p>
              </div>
            </div>

            <div className={styles.featureCard}>
              <div className={styles.iconContainer}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              </div>
              <div>
                <h3 className={styles.cardTitle}>Duplicate Prevention & Automerge</h3>
                <p className={styles.cardDesc}>Define custom duplicate rules to cleanly ingest new excel lists and merge duplicates.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side: Interactive Dashboard Simulator */}
        <div ref={simPanelRef} className={styles.simulatorWrapper}>
          <div className={styles.simHeader}>
            <div className={styles.dots}>
              <span className={styles.dot}></span>
              <span className={styles.dot}></span>
              <span className={styles.dot}></span>
            </div>
            <div className={styles.panelTitle}>ToxBox CRM Pipeline Simulation</div>
            <button 
              className={`${styles.simButton} ${isSimulating ? styles.simActive : ''}`} 
              onClick={handleStartSimulation}
              disabled={isSimulating}
            >
              {isSimulating ? "Simulating..." : "Simulate Live Campaign"}
            </button>
          </div>

          <div className={styles.simBody}>
            {/* Lead list */}
            <div className={styles.leadsContainer}>
              <div className={styles.leadsTitle}>Active Campaign Leads</div>
              <div className={styles.leadsList}>
                {leads.map(lead => (
                  <div 
                    key={lead.id} 
                    className={`${styles.leadItem} ${selectedLeadId === lead.id ? styles.selectedLead : ''}`}
                    onClick={() => setSelectedLeadId(lead.id)}
                  >
                    <div className={styles.leadMain}>
                      <div className={styles.leadName}>{lead.name}</div>
                      <div className={styles.leadEmail}>{lead.email}</div>
                    </div>
                    <div className={styles.leadBadges}>
                      <span className={`${styles.statusBadge} ${styles[lead.status.toLowerCase().replace(" ", "")]}`}>
                        {lead.status}
                      </span>
                      {lead.verified ? (
                        <span className={styles.verifiedBadge} title="SMTP Verified">✓</span>
                      ) : (
                        <span className={styles.unverifiedBadge} title="Pending Verification">?</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Simulation Steps & Detailed logs */}
            <div className={styles.detailsContainer}>
              <div className={styles.statsOverview}>
                <div className={styles.statBox}>
                  <div className={styles.statLabel}>Email Open Rate</div>
                  <div className={styles.statValue}>
                    {simulationStep >= 3 ? "66%" : "0%"}
                  </div>
                </div>
                <div className={styles.statBox}>
                  <div className={styles.statLabel}>Click Rate (CTR)</div>
                  <div className={styles.statValue}>
                    {simulationStep >= 4 ? "33%" : "0%"}
                  </div>
                </div>
              </div>

              <div className={styles.leadDetailsPanel}>
                <div className={styles.detailsHeader}>
                  Activity Timeline: <span>{selectedLead.name}</span>
                </div>
                <div className={styles.timelineList}>
                  {selectedLead.timeline.map((event, idx) => (
                    <div key={idx} className={styles.timelineItem}>
                      <span className={styles.timelineDot}></span>
                      <span className={styles.timelineText}>{event}</span>
                    </div>
                  ))}
                  {isSimulating && selectedLead.id === 1 && simulationStep === 1 && (
                    <div className={styles.timelineItemPending}>
                      <span className={styles.pulseDot}></span>
                      <span className={styles.timelineText}>Verifying email address...</span>
                    </div>
                  )}
                  {isSimulating && selectedLead.id === 1 && simulationStep === 2 && (
                    <div className={styles.timelineItemPending}>
                      <span className={styles.pulseDot}></span>
                      <span className={styles.timelineText}>Sending email...</span>
                    </div>
                  )}
                  {isSimulating && selectedLead.id === 1 && simulationStep === 3 && (
                    <div className={styles.timelineItemPending}>
                      <span className={styles.pulseDot}></span>
                      <span className={styles.timelineText}>Waiting for recipient to open...</span>
                    </div>
                  )}
                  {isSimulating && selectedLead.id === 1 && simulationStep === 4 && (
                    <div className={styles.timelineItemPending}>
                      <span className={styles.pulseDot}></span>
                      <span className={styles.timelineText}>Waiting for link clicks...</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Bottom simulation indicator bar */}
          {isSimulating && (
            <div className={styles.progressBarWrapper}>
              <div 
                className={styles.progressBar} 
                style={{ width: `${(simulationStep / 5) * 100}%` }}
              ></div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default CRMLeadManagementSection;
